import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MONTHS, URLS } from '../../../resources/constants';
import { useCreditCardApi } from '../../../resources/hooks/api/creditCardApiHook';
import { getBranch } from '../../../store/user/selectors';
import CreditCardGlClassificationStatus from '../CreditCardGlClassification/CreditCardGlClassificationStatus';
import DepositGlClassification from '../DepositGlClassification/DepositGlClassification';
import ExpenseGlClassification from '../ExpenseGlClassification/ExpenseGlClassification';
import RevenueGlClassification from '../RevenueGlClassification/RevenueGlClassification';
import '../GlClassification.css';
import { useGlClassificationApi } from 'resources/hooks/api/glClassificationApi';

type TabKey = 'deposit' | 'expense' | 'revenue' | 'creditCard';

const STEPS: { key: TabKey; label: string; icon: string; accountType?: number }[] = [
    { key: 'deposit', label: 'Deposits', icon: 'fa-university', accountType: 3 },
    { key: 'expense', label: 'Expenses & Vendors', icon: 'fa-receipt', accountType: 1 },
    { key: 'revenue', label: 'Revenue & Sales', icon: 'fa-chart-line', accountType: 2 },
    { key: 'creditCard', label: 'Credit Cards', icon: 'fa-credit-card' },
];

const isPending = (rec: any) => !!rec?.status && rec.status !== 'posted';
const pad = (v: string | number) => String(v).padStart(2, '0');

const UnifiedGlClassification: React.FC = () => {
    const { month: urlMonth, year: urlYear } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation('scenes.glclassification.unified');
    const branchData = useSelector(getBranch);
    const { classificationListStatus } = useGlClassificationApi();
    const { fetchCreditCardCsvList } = useCreditCardApi();

    const [records, setRecords] = useState<Record<'deposit' | 'expense' | 'revenue', any[]>>({
        deposit: [],
        expense: [],
        revenue: [],
    });
    const [creditCardPendingCount, setCreditCardPendingCount] = useState<number>(0);

    const now = useMemo(() => new Date(), []);
    const todayMonth = pad(now.getMonth() + 1);
    const todayYear = String(now.getFullYear());

    const activeMonth = pad(urlMonth || now.getMonth() + 1);
    const activeYear = String(urlYear || now.getFullYear());
    const activeTab: TabKey = (STEPS.find((s) => s.key === searchParams.get('tab'))?.key as TabKey) || 'deposit';
    const activeIndex = STEPS.findIndex((s) => s.key === activeTab);
    const isCurrentPeriod = activeMonth === todayMonth && activeYear === todayYear;

    const goTo = useCallback(
        (month: string, year: string | number, tab: TabKey = activeTab) =>
            navigate(`/gl-classification/${month}/${year}?tab=${tab}`),
        [navigate, activeTab]
    );

    /* ---------- data ---------- */

    const fetchCreditCardCount = useCallback(
        (branchId: number) => {
            fetchCreditCardCsvList(
                { branch_id: branchId, status: 0, per_page: 1 },
                (_msg: string, resp: any) => {
                    const total = resp?.data?.data?.total ?? 0;
                    setCreditCardPendingCount(total);
                },
                () => {
                    setCreditCardPendingCount(0);
                }
            );
        },
        [fetchCreditCardCsvList]
    );

    const fetchAllStatuses = useCallback(
        (branchId: number) => {
            STEPS.forEach(({ key, accountType }) => {
                if (accountType) {
                    classificationListStatus(
                        { account_type: accountType, branch_id: branchId },
                        (_msg: string, resp: any) => {
                            const transactions = resp?.data?.data?.transactions || [];
                            setRecords((prev) => ({ ...prev, [key]: transactions }));
                        },
                        (err: any) => {
                            console.error(`[GL Classification] Failed to fetch ${key} status:`, err);
                        }
                    );
                }
            });
            fetchCreditCardCount(branchId);
        },
        [classificationListStatus, fetchCreditCardCount]
    );

    useEffect(() => {
        if (branchData?.id) fetchAllStatuses(+branchData.id);
    }, [branchData?.id]);

    const pendingCounts = useMemo(
        () => ({
            deposit: records.deposit.filter(isPending).length,
            expense: records.expense.filter(isPending).length,
            revenue: records.revenue.filter(isPending).length,
            creditCard: creditCardPendingCount,
        }),
        [records, creditCardPendingCount]
    );

    const isNonMonthTab = activeTab === 'creditCard';
    const currentTabRecords = isNonMonthTab ? [] : records[activeTab];

    const monthStatusMap = useMemo(() => {
        const map = new Map<string, string>();
        currentTabRecords.forEach((rec: any) => {
            if (rec?.item?.month && rec?.item?.year) {
                map.set(`${rec.item.year}-${pad(rec.item.month)}`, rec.status);
            }
        });
        return map;
    }, [currentTabRecords]);

    const getStepLabel = useCallback(
        (key: TabKey) => {
            switch (key) {
                case 'deposit':
                    return t('steps.deposit', { defaultValue: 'Deposits' });
                case 'expense':
                    return t('steps.expense', { defaultValue: 'Expenses & Vendors' });
                case 'revenue':
                    return t('steps.revenue', { defaultValue: 'Revenue & Sales' });
                case 'creditCard':
                    return t('steps.credit_card', { defaultValue: 'Credit Cards' });
            }
        },
        [t]
    );

    const monthLabelOf = useCallback(
        (value: string | number) => {
            const padded = pad(value);
            const translated = t(`month.${padded}`, { defaultValue: '' });
            if (translated) return translated;
            const found = MONTHS.find((m: any) => +m.value === +value);
            return found ? found.label : `Month ${value}`;
        },
        [t]
    );

    // Oldest pending period first, so the shortcut always walks forward in time.
    const firstPendingPeriod = useMemo(() => {
        if (isNonMonthTab) return null;
        const list = currentTabRecords
            .filter((rec: any) => isPending(rec) && rec?.item?.month && rec?.item?.year)
            .map((rec: any) => ({
                month: pad(rec.item.month),
                year: String(rec.item.year),
            }))
            .sort((a, b) => (a.year === b.year ? a.month.localeCompare(b.month) : a.year.localeCompare(b.year)));
        return list.find((p) => p.month !== activeMonth || p.year !== activeYear) || null;
    }, [currentTabRecords, activeMonth, activeYear, activeTab]);

    const periodStatus = monthStatusMap.get(`${activeYear}-${activeMonth}`);
    const monthLabel = monthLabelOf(activeMonth);

    const availableYears = useMemo(() => {
        const y = now.getFullYear();
        const years = new Set<string>();
        for (let i = y - 3; i <= y + 1; i++) years.add(String(i));
        years.add(activeYear); // never let the select lose its current value
        return Array.from(years).sort();
    }, [now, activeYear]);

    /* ---------- handlers ---------- */

    const handleTabChange = (tab: TabKey) => setSearchParams({ tab });

    const stepMonth = (offset: number) => {
        let m = +activeMonth + offset;
        let y = +activeYear;
        if (m < 1) {
            m = 12;
            y -= 1;
        } else if (m > 12) {
            m = 1;
            y += 1;
        }
        goTo(pad(m), y);
    };

    const advanceTo = (next: TabKey | null) => {
        if (next) handleTabChange(next);
        if (branchData?.id) fetchAllStatuses(+branchData.id);
    };

    /* ---------- render ---------- */

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid px-2 px-md-3">
                <header className="gl-shell">
                    {/* Row 1: where am I + which period */}
                    <div className="gl-head">
                        <div className="gl-head__title">
                            <Link
                                to={URLS.DASHBOARD}
                                className="gl-icon-btn"
                                title={t('header.back_to_dashboard', { defaultValue: 'Back to dashboard' })}
                                aria-label={t('header.back_to_dashboard', { defaultValue: 'Back to dashboard' })}
                            >
                                <i className="fa fa-arrow-left" />
                            </Link>
                            <div className="gl-head__text">
                                <h1 className="gl-head__h1">{t('title', { defaultValue: 'GL Classification' })}</h1>
                                <div className="gl-head__sub">
                                    <span className="gl-head__period">
                                        {activeTab === 'creditCard'
                                            ? t('header.statement_batches', { defaultValue: 'Statement Batches' })
                                            : `${monthLabel} ${activeYear}`}
                                    </span>
                                    {!isNonMonthTab && periodStatus && (
                                        <span className={`gl-chip ${periodStatus === 'posted' ? 'gl-chip--done' : 'gl-chip--pending'}`}>
                                            <i className={`fa ${periodStatus === 'posted' ? 'fa-check' : 'fa-clock'}`} />
                                            {periodStatus === 'posted'
                                                ? t('status.posted', { defaultValue: 'Posted' })
                                                : t('status.pending', { defaultValue: 'Pending' })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {!isNonMonthTab && (
                            <div className="gl-period" role="group" aria-label={t('period.aria_period', { defaultValue: 'Period' })}>
                                <button
                                    type="button"
                                    className="gl-icon-btn"
                                    onClick={() => stepMonth(-1)}
                                    title={t('period.prev_month', { defaultValue: 'Previous month' })}
                                    aria-label={t('period.prev_month', { defaultValue: 'Previous month' })}
                                >
                                    <i className="fa fa-chevron-left" />
                                </button>
                                <select
                                    className="gl-select gl-select--month"
                                    value={activeMonth}
                                    onChange={(e) => goTo(e.target.value, activeYear)}
                                    aria-label={t('period.aria_month', { defaultValue: 'Month' })}
                                >
                                    {MONTHS.map((m: any) => {
                                        const value = pad(m.value);
                                        const status = monthStatusMap.get(`${activeYear}-${value}`);
                                        const suffix = !status
                                            ? ''
                                            : status === 'posted'
                                                ? `  ✓ ${t('status.posted', { defaultValue: 'Posted' })}`
                                                : `  • ${t('status.pending', { defaultValue: 'Pending' })}`;
                                        return (
                                            <option key={m.value} value={value}>
                                                {monthLabelOf(m.value)}
                                                {suffix}
                                            </option>
                                        );
                                    })}
                                </select>
                                <select
                                    className="gl-select gl-select--year"
                                    value={activeYear}
                                    onChange={(e) => goTo(activeMonth, e.target.value)}
                                    aria-label={t('period.aria_year', { defaultValue: 'Year' })}
                                >
                                    {availableYears.map((yr) => (
                                        <option key={yr} value={yr}>
                                            {yr}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="gl-icon-btn"
                                    onClick={() => stepMonth(1)}
                                    title={t('period.next_month', { defaultValue: 'Next month' })}
                                    aria-label={t('period.next_month', { defaultValue: 'Next month' })}
                                >
                                    <i className="fa fa-chevron-right" />
                                </button>
                                <button
                                    type="button"
                                    className="gl-text-btn"
                                    onClick={() => goTo(todayMonth, todayYear)}
                                    disabled={isCurrentPeriod}
                                    title={t('period.go_to_current_month', { defaultValue: 'Go to the current month' })}
                                >
                                    {t('period.this_month', { defaultValue: 'This month' })}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Row 2: the workflow steps (Deposits -> Expenses -> Revenue -> Credit Cards) */}
                    <nav className="gl-steps" role="tablist" aria-label={t('steps.aria_steps', { defaultValue: 'Classification steps' })}>
                        {STEPS.map((step, i) => {
                            const count = pendingCounts[step.key];
                            const selected = step.key === activeTab;
                            const complete = count === 0;
                            return (
                                <button
                                    key={step.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    className={`gl-step ${selected ? 'is-active' : ''} ${complete ? 'is-complete' : 'is-open'}`}
                                    onClick={() => handleTabChange(step.key)}
                                >
                                    <span className="gl-step__marker" aria-hidden="true">
                                        {complete ? <i className="fa fa-check" /> : i + 1}
                                    </span>
                                    <span className="gl-step__body">
                                        <span className="gl-step__label">
                                            <i className={`fa ${step.icon}`} aria-hidden="true" /> {getStepLabel(step.key)}
                                        </span>
                                        <span className="gl-step__meta">
                                            {step.key === 'creditCard'
                                                ? complete
                                                    ? t('meta.all_batches_classified', { defaultValue: 'All batches classified' })
                                                    : count === 1
                                                        ? t('meta.single_batch_pending', { count, defaultValue: '1 batch pending' })
                                                        : t('meta.multiple_batches_pending', { count, defaultValue: `${count} batches pending` })
                                                : complete
                                                    ? t('meta.all_months_posted', { defaultValue: 'All months posted' })
                                                    : count === 1
                                                        ? t('meta.single_month_pending', { count, defaultValue: '1 month pending' })
                                                        : t('meta.multiple_months_pending', { count, defaultValue: `${count} months pending` })}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                        <span
                            className="gl-steps__progress"
                            style={{ transform: `translateX(${activeIndex * 100}%)`, width: `${100 / STEPS.length}%` }}
                            aria-hidden="true"
                        />
                    </nav>

                    {/* Quiet nudge instead of a loud header button */}
                    {firstPendingPeriod && (
                        <div className="gl-nudge" role="status">
                            <i className="fa fa-exclamation-circle" aria-hidden="true" />
                            <span>
                                {t('nudge.message_prefix', {
                                    step: getStepLabel(STEPS[activeIndex].key),
                                    defaultValue: `${getStepLabel(STEPS[activeIndex].key)} still has pending work in `,
                                })}
                                <strong>
                                    {monthLabelOf(firstPendingPeriod.month)} {firstPendingPeriod.year}
                                </strong>
                                .
                            </span>
                            <button
                                type="button"
                                className="gl-nudge__action"
                                onClick={() => goTo(firstPendingPeriod.month, firstPendingPeriod.year)}
                            >
                                {t('nudge.action', {
                                    month: monthLabelOf(firstPendingPeriod.month),
                                    year: firstPendingPeriod.year,
                                    defaultValue: `Go to ${monthLabelOf(firstPendingPeriod.month)} ${firstPendingPeriod.year}`,
                                })}
                            </button>
                        </div>
                    )}
                </header>

                <div className="tab-content">
                    {activeTab === 'deposit' && (
                        <DepositGlClassification
                            propMonth={activeMonth}
                            propYear={activeYear}
                            hideHeaderNavigation={true}
                            onNextTab={() => advanceTo('expense')}
                            nextTabLabel={t('actions.continue_to_expense', { defaultValue: 'Continue to Expenses & Vendors' })}
                        />
                    )}
                    {activeTab === 'expense' && (
                        <ExpenseGlClassification
                            propMonth={activeMonth}
                            propYear={activeYear}
                            hideHeaderNavigation={true}
                            onNextTab={() => advanceTo('revenue')}
                            nextTabLabel={t('actions.continue_to_revenue', { defaultValue: 'Continue to Revenue & Sales' })}
                        />
                    )}
                    {activeTab === 'revenue' && (
                        <RevenueGlClassification
                            propMonth={activeMonth}
                            propYear={activeYear}
                            hideHeaderNavigation={true}
                            onNextTab={() => advanceTo('creditCard')}
                            nextTabLabel={t('actions.continue_to_credit_card', { defaultValue: 'Continue to Credit Cards' })}
                        />
                    )}
                    {activeTab === 'creditCard' && (
                        <CreditCardGlClassificationStatus
                            hideHeaderNavigation={true}
                            onNextTab={() => navigate(URLS.BANK_RECONCILE)}
                            nextTabLabel={t('actions.finish_and_reconcile', { defaultValue: 'Finish & Reconcile Bank' })}
                            onBatchChanged={() => {
                                if (branchData?.id) fetchCreditCardCount(+branchData.id);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default UnifiedGlClassification;
